import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

function RevealDiv({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('ew-revealed'); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`ew-reveal ${className ?? ''}`} style={style}>{children}</div>;
}

const styles = [
  {
    num: '01',
    title: 'The Classic Goddess Gown',
    difficulty: 'Easiest',
    vibe: 'Regal & Flowing',
    steps: [
      'Hold the dress up by the two top corners.',
      'Slip it over your head, letting the fabric fall naturally.',
      'Adjust so the neckline sits comfortably.',
      'Let it flow to the floor — you\'re done. You\'re a goddess.',
    ],
    tip: 'Perfect for festivals, events, or any day you want to feel like royalty.',
    img: 'https://public.readdy.ai/ai/img_res/2d0d062ec51081b4ab5355bbb0b4d74c.jpg',
  },
  {
    num: '02',
    title: 'The Wrap Dress',
    difficulty: 'Easy',
    vibe: 'Fitted & Flattering',
    steps: [
      'Put the dress on in classic gown style.',
      'Gather the excess fabric at your waist.',
      'Wrap it around your body once or twice.',
      'Tie or tuck the end securely at your hip.',
    ],
    tip: 'This style is incredibly flattering on every body type — it creates a beautiful silhouette.',
    img: 'https://readdy.ai/api/search-image?query=Moroccan%20convertible%20dress%20worn%20as%20wrap%20dress%20fitted%20silhouette%20model%20warm%20studio%20lighting%20bohemian%20fashion%20beautiful%20flowing%20fabric%20tied%20at%20waist&width=500&height=600&seq=howto-002&orientation=portrait',
  },
  {
    num: '03',
    title: 'The Off-Shoulder Drape',
    difficulty: 'Easy',
    vibe: 'Relaxed & Bohemian',
    steps: [
      'Start with the classic gown style.',
      'Pull one shoulder of the fabric down off your shoulder.',
      'Drape it loosely across your arm.',
      'Adjust the neckline to sit asymmetrically.',
    ],
    tip: 'Great for warm days or beach events — effortlessly chic.',
    img: 'https://readdy.ai/api/search-image?query=Moroccan%20convertible%20dress%20worn%20off%20shoulder%20bohemian%20style%20model%20warm%20studio%20lighting%20flowing%20fabric%20relaxed%20elegant%20fashion&width=500&height=600&seq=howto-003&orientation=portrait',
  },
  {
    num: '04',
    title: 'The Tunic & Pants Look',
    difficulty: 'Easy',
    vibe: 'Casual & Chic',
    steps: [
      'Put on your favorite leggings or wide-leg pants.',
      'Wear the Magic Dress over them as a long tunic.',
      'Belt at the waist for definition, or leave it loose.',
      'Add a scarf for extra goddess energy.',
    ],
    tip: 'This is a great everyday look — comfortable, stylish, and totally unique.',
    img: 'https://readdy.ai/api/search-image?query=Moroccan%20convertible%20dress%20worn%20as%20tunic%20over%20pants%20leggings%20model%20warm%20studio%20lighting%20bohemian%20fashion%20layered%20casual%20chic%20style&width=500&height=600&seq=howto-004&orientation=portrait',
  },
  {
    num: '05',
    title: 'The Halter Style',
    difficulty: 'Moderate',
    vibe: 'Bold & Confident',
    steps: [
      'Hold the dress with the top edge at your chest.',
      'Bring the two top corners up and behind your neck.',
      'Tie them securely at the nape of your neck.',
      'Adjust the front for your preferred coverage.',
    ],
    tip: 'Shows off your shoulders beautifully — perfect for summer events.',
    img: 'https://readdy.ai/api/search-image?query=Moroccan%20convertible%20dress%20worn%20as%20halter%20style%20model%20warm%20studio%20lighting%20bohemian%20fashion%20shoulders%20exposed%20confident%20bold%20style&width=500&height=600&seq=howto-005&orientation=portrait',
  },
  {
    num: '06',
    title: 'The Maxi Skirt',
    difficulty: 'Easy',
    vibe: 'Versatile & Fun',
    steps: [
      'Step into the dress and pull it up to your waist.',
      'Gather the top edge and fold it over once.',
      'Wrap and tuck or tie the waistband securely.',
      'Pair with your favorite top or crop.',
    ],
    tip: 'Instantly transforms your wardrobe — one dress, two completely different outfits.',
    img: 'https://readdy.ai/api/search-image?query=Moroccan%20convertible%20dress%20worn%20as%20maxi%20skirt%20model%20warm%20studio%20lighting%20bohemian%20fashion%20flowing%20skirt%20paired%20with%20top%20versatile%20style&width=500&height=600&seq=howto-006&orientation=portrait',
  },
];

const difficultyColor: Record<string, string> = {
  'Easiest': '#1E4F4F',
  'Easy': '#2C6B6B',
  'Moderate': '#C9933A',
};

export default function MagicDressHowTos() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative py-24 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://readdy.ai/api/search-image?query=woman%20joyfully%20styling%20a%20flowing%20Moroccan%20convertible%20goddess%20dress%20multiple%20ways%20warm%20golden%20light%20playful%20bohemian%20fashion%20editorial%20photography%20empowering&width=1920&height=600&seq=howto-hero-001&orientation=landscape" alt="Magic Dress How-To hero" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-black/65"></div>
          </div>
          <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
            <p style={{ fontFamily: '"Dancing Script", cursive', color: '#E8B96A', fontSize: '1.3rem' }} className="mb-2">One dress. Infinite you.</p>
            <h1 style={{ fontFamily: '"Playfair Display", serif', color: '#fff' }} className="text-5xl md:text-6xl font-black mb-4">Magic Dress<br /><em style={{ color: '#E8B96A' }}>How-To&apos;s</em></h1>
            <p style={{ fontFamily: '"Lato", sans-serif', color: '#EDE5D8', lineHeight: 1.8 }} className="text-lg mb-8">
              The Magic Dress is wearable 10+ different ways. Here are 6 of our favorites — from effortless goddess gown to chic everyday tunic. No sewing required. Just you, the dress, and a little magic.
            </p>
            <a href="#styles" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
              See the Styles <i className="ri-arrow-down-line"></i>
            </a>
          </div>
        </section>

        {/* Intro strip */}
        <section style={{ backgroundColor: '#7A2E2E' }} className="py-6">
          <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-center gap-6 md:gap-10">
            {['10+ Ways to Wear', 'Regular & Plus Sizes', 'Moroccan-Inspired', 'No Experience Needed'].map(t => (
              <span key={t} className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide whitespace-nowrap" style={{ fontFamily: '"Lato", sans-serif', color: '#E8B96A' }}>
                <i className="ri-magic-line"></i> {t}
              </span>
            ))}
          </div>
        </section>

        {/* Styles */}
        <section id="styles" className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <RevealDiv className="text-center mb-14">
              <p style={{ fontFamily: '"Dancing Script", cursive', color: '#C9933A', fontSize: '1.2rem' }}>Step-by-step styling guide</p>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-4xl font-bold mt-1">6 Ways to Wear Your Magic Dress</h2>
            </RevealDiv>

            <div className="flex flex-col gap-20">
              {styles.map((s, i) => (
                <RevealDiv key={s.num}>
                  <div className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-10`}>
                    {/* Image */}
                    <div className="flex-1 w-full">
                      <div className="rounded-2xl overflow-hidden" style={{ height: '420px' }}>
                        <img src={s.img} alt={s.title} className="w-full h-full object-cover object-top" />
                      </div>
                    </div>
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <p style={{ fontFamily: '"Playfair Display", serif', color: '#C9933A', fontSize: '3.5rem', fontWeight: 900, lineHeight: 1, opacity: 0.3 }}>{s.num}</p>
                        <div>
                          <span className="text-xs font-bold px-3 py-1 rounded-full mr-2" style={{ backgroundColor: difficultyColor[s.difficulty] || '#1E4F4F', color: '#E8B96A', fontFamily: '"Lato", sans-serif' }}>{s.difficulty}</span>
                          <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ backgroundColor: '#EDE5D8', color: '#4A3728', fontFamily: '"Lato", sans-serif' }}>{s.vibe}</span>
                        </div>
                      </div>
                      <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-3xl font-bold mb-5 -mt-2">{s.title}</h2>
                      <ol className="flex flex-col gap-3 mb-6">
                        {s.steps.map((step, si) => (
                          <li key={si} className="flex items-start gap-3">
                            <span className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-bold" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>{si + 1}</span>
                            <p style={{ fontFamily: '"Lato", sans-serif', color: '#4A3728', lineHeight: 1.7 }} className="text-base">{step}</p>
                          </li>
                        ))}
                      </ol>
                      <div className="p-4 rounded-xl flex items-start gap-3" style={{ backgroundColor: '#F5F0E8' }}>
                        <i className="ri-lightbulb-line text-lg flex-shrink-0 mt-0.5" style={{ color: '#C9933A' }}></i>
                        <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42', lineHeight: 1.7 }} className="text-sm italic">{s.tip}</p>
                      </div>
                    </div>
                  </div>
                </RevealDiv>
              ))}
            </div>
          </div>
        </section>

        {/* CTA back to shop */}
        <RevealDiv>
          <section className="py-20" style={{ background: 'linear-gradient(135deg, #C9933A 0%, #7A2E2E 100%)' }}>
            <div className="max-w-3xl mx-auto px-4 text-center">
              <p style={{ fontFamily: '"Dancing Script", cursive', color: '#fff', fontSize: '1.3rem' }} className="mb-2">Ready to get your own?</p>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#fff' }} className="text-4xl font-bold mb-4">Shop the Magic Dress</h2>
              <p style={{ fontFamily: '"Lato", sans-serif', color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }} className="text-base mb-8">
                Available in regular and plus sizes. Multiple colors and styles. Call or email us to find your perfect Magic Dress.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/magic-dresses" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#fff', color: '#7A2E2E', fontFamily: '"Lato", sans-serif' }}>
                  <i className="ri-store-line"></i> See the Magic Dress
                </Link>
                <a href="tel:7346468620" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap border-2 border-white transition-all duration-200 hover:-translate-y-0.5" style={{ color: '#fff', fontFamily: '"Lato", sans-serif', backgroundColor: 'transparent' }}>
                  <i className="ri-phone-line"></i> Call Us
                </a>
              </div>
            </div>
          </section>
        </RevealDiv>
      </main>
      <Footer />
    </div>
  );
}
